/**
 * Streetscape — ground patches (tessellated so the baked contact dirt reads),
 * chamfered kerbs, pavements, drains, road markings, cracks and speed bumps.
 *
 * owner: level
 */
import * as THREE from 'three';
import { chamferBox, chamferCyl, quadXZ, kerbProfile, xform, alignX, segLength } from '../Geo.js';

/**
 * A tessellated ground patch. Tessellation matters: the vertex-colour dirt bake
 * needs vertices near walls, and a 2-triangle quad has none.
 *
 * `collide` defaults to TRUE: a walkable surface that is not in
 * `ctx.level.collision` is a hole in the world, and defaulting it off meant
 * every patch had to remember to opt in — which is exactly how the map ended up
 * with no floor at all. Pass `collide: false` only for decoration that sits on
 * top of a surface that already collides (drifts, cracks, road paint), or for
 * far terrain nobody can reach.
 *
 * The proxy is a 0.4 m slab under the surface, merged with every other blocker
 * of the same material into one invisible mesh — never render geometry.
 */
export function groundPatch(w, { x0, z0, x1, z1, y = 0, mat = 'sand', surface = 'sand', res = 0.8, tint = null, uvScale = 2.4, collide = true, cast = false }) {
  const X0 = Math.min(x0, x1), X1 = Math.max(x0, x1);
  const Z0 = Math.min(z0, z1), Z1 = Math.max(z0, z1);
  const W = X1 - X0, D = Z1 - Z0;
  const sx = Math.max(1, Math.round(W / res));
  const sz = Math.max(1, Math.round(D / res));
  const g = new THREE.PlaneGeometry(W, D, sx, sz);
  g.rotateX(-Math.PI / 2);
  w.batch.add(g, mat, {
    matrix: xform([(X0 + X1) / 2, y, (Z0 + Z1) / 2], 0),
    surface, uvScale, tint, cast,
  });
  g.dispose();
  w.zone(X0, Z0, X1, Z1, y, surface);
  if (collide) w.blockerBox(X0, y - 0.4, Z0, X1, y, Z1, surface, false);
  return { X0, X1, Z0, Z1 };
}

/**
 * An organic sand/dirt drift over paving. A ragged disc reads as weather;
 * an axis-aligned rectangle reads as a bug.
 */
export function driftPatch(w, { x, z, rx = 3, rz = 2, y = 0.008, rot = 0, mat = 'sand', surface = 'sand', tint = null, seg = 20, uvScale = 2.6 }) {
  const rng = w.rng;
  const g = new THREE.CircleGeometry(1, seg);
  g.rotateX(-Math.PI / 2);
  const pos = g.attributes.position;
  // ragged rim: perturb every ring vertex (index 0 is the fan centre)
  const jit = [];
  for (let i = 0; i <= seg; i++) jit.push(rng.range(0.62, 1.0));
  for (let i = 1; i < pos.count; i++) {
    const k = (i - 1) % (seg + 1);
    const f = jit[k];
    pos.setX(i, pos.getX(i) * f);
    pos.setZ(i, pos.getZ(i) * f);
  }
  g.scale(rx, 1, rz);
  w.batch.add(g, mat, { matrix: xform([x, y, z], rot), surface, uvScale, tint, cast: false });
  g.dispose();
}

/* ------------------------------------------------------------------ *
 * killing the ground tile
 * ------------------------------------------------------------------ */

/*
 * The measured defect: `planarUV` maps world position through `1/uvScale`, so a
 * patch authored at `uvScale: 1.9` repeats with a **1.9 m period**. At the eye
 * height and FOV of `level_square` and `wpn_rifle_hip` that period lands at
 * roughly 300 px in the lower third of the frame — which is exactly the
 * "readable ~300 px gravel tile" the critic found, and `CRITIC.md` §2 makes any
 * findable tile an instant fail.
 *
 * A single flat surface cannot escape its own period. What breaks it is
 * *interference*: a second and third layer of the same or a neighbouring
 * material at periods that share no small common multiple with the base
 * (1.9 against 2.7, 4.3, 6.1 — all mutually irrational-ish), each with a ragged
 * boundary so there is no repeating edge either. The eye can find one lattice;
 * it cannot find three superimposed lattices at incommensurate pitches.
 *
 * Materials owns the surface itself (detail normals, macro variation in the
 * texture); this is the level side of the same fix, and it costs nothing but
 * triangles because every layer lands in a batch group that already exists.
 */
const BREAK_PITCH = [2.73, 4.31, 6.11, 3.37, 5.23, 1.47];

/*
 * `Batcher` keys a merged group on `mat|surface|cast|recv`, so passing a
 * surface that does not match the one the rest of the map already uses for that
 * material opens a WHOLE NEW DRAW CALL for a handful of pebbles. Every helper
 * in this section resolves its surface through this table instead of taking a
 * guess at the call site. Measured: getting it wrong on `pebbleScatter`,
 * `tyreTracks` and `latticeTower` cost exactly 3 calls.
 */
const GROUND_SURFACE = {
  sand: 'sand', gravel: 'dirt', dirt: 'dirt', asphalt: 'concrete',
  concrete_floor: 'concrete', concrete_wall: 'concrete',
};
const surfOf = (mat, given) => given ?? GROUND_SURFACE[mat] ?? 'concrete';

/**
 * Break a ground plane's UV period without repainting it.
 *
 * TWO LAYERS, and the split matters — the first version of this function got it
 * wrong and turned `level_square`'s paving into a camouflage pattern, which is a
 * worse defect than the tile it was curing:
 *
 *   PHASE  large patches of the HOST material at a different UV pitch and
 *          essentially no tint (±5 % luminance). They are what actually kills
 *          the lattice, and because the material and the value both match, the
 *          patch boundary is invisible — you cannot see them at all, only the
 *          absence of the repeat.
 *   WEAR   small patches carrying real value variation, and *occasionally* a
 *          neighbouring material, pulled hard toward the host's own value so it
 *          reads as a worn patch rather than as a second paint.
 *
 * The lesson, which is the same one FINDINGS F15 records about hue spread on the
 * weapon: the cure for a uniform surface is not a varied surface, it is an
 * aperiodic one. Value contrast is a separate axis and a little goes a long way.
 */
export function groundBlend(w, {
  x0, z0, x1, z1, y = 0.006, host, guests = [], avoid = [],
  phase = 14, wear = 26, phaseR = [2.2, 6.4], wearR = [0.55, 2.1],
  wearValue = [0.86, 1.12], guestChance = 0.28, guestPull = 0.65,
}) {
  const rng = w.rng;
  const X0 = Math.min(x0, x1), X1 = Math.max(x0, x1);
  const Z0 = Math.min(z0, z1), Z1 = Math.max(z0, z1);
  const blocked = (x, z) => avoid.some((a) => x > a[0] && x < a[2] && z > a[1] && z < a[3]);
  const pick = () => {
    for (let t = 0; t < 6; t++) {
      const cx = rng.range(X0, X1), cz = rng.range(Z0, Z1);
      if (!blocked(cx, cz)) return [cx, cz];
    }
    return null;
  };
  /** A value multiplier with only a whisper of channel skew — never a hue. */
  const val = (v, warm = 0) => [v * (1 + warm * 0.035), v, v * (1 - warm * 0.045)];

  for (let i = 0; i < phase; i++) {
    const p = pick();
    if (!p) continue;
    driftPatch(w, {
      x: p[0], z: p[1],
      rx: rng.range(phaseR[0], phaseR[1]), rz: rng.range(phaseR[0], phaseR[1]) * rng.range(0.6, 1.0),
      rot: rng.range(0, Math.PI), y: y + (i % 4) * 0.0014,
      mat: host, surface: surfOf(host), seg: rng.int(24, 32),
      uvScale: BREAK_PITCH[i % BREAK_PITCH.length] * rng.range(0.88, 1.14),
      tint: val(rng.range(0.955, 1.05), rng.range(-1, 1)),
    });
  }
  for (let i = 0; i < wear; i++) {
    const p = pick();
    if (!p) continue;
    const guest = guests.length && rng() < guestChance;
    const mat = guest ? guests[rng.int(0, guests.length - 1)] : host;
    // a guest material is pulled toward the host's value so two different
    // albedos never sit side by side at full strength
    const v = rng.range(wearValue[0], wearValue[1]);
    const k = guest ? 1 + (v - 1) * guestPull : v;
    driftPatch(w, {
      x: p[0], z: p[1],
      rx: rng.range(wearR[0], wearR[1]), rz: rng.range(wearR[0], wearR[1]) * rng.range(0.6, 1.2),
      rot: rng.range(0, Math.PI), y: y + 0.006 + (i % 3) * 0.0012,
      mat, surface: surfOf(mat), seg: rng.int(18, 26),
      uvScale: BREAK_PITCH[(i + 3) % BREAK_PITCH.length] * rng.range(0.8, 1.2),
      tint: val(k, rng.range(-1, 1)),
    });
  }
}

/**
 * Loose stone. Merged rather than instanced: a 44-triangle chamfered pebble in
 * an existing batch group costs nothing per draw, whereas 400 more instances of
 * `rubble_chunk` (396 tris each) would cost 158 k triangles for the same read.
 */
export function pebbleScatter(w, {
  x0, z0, x1, z1, y = 0.01, count = 160, size = [0.045, 0.16],
  mat = 'gravel', avoid = [], clump = 0.45,
}) {
  const rng = w.rng;
  const blocked = (x, z) => avoid.some((a) => x > a[0] && x < a[2] && z > a[1] && z < a[3]);
  let cx = rng.range(x0, x1), cz = rng.range(z0, z1);
  for (let i = 0; i < count; i++) {
    // stones gather in drifts against nothing in particular; a Poisson-flat
    // scatter reads as a decal grid, which is the thing we are curing
    if (rng() > clump) { cx = rng.range(x0, x1); cz = rng.range(z0, z1); }
    const px = cx + rng.range(-0.9, 0.9), pz = cz + rng.range(-0.9, 0.9);
    if (px < x0 || px > x1 || pz < z0 || pz > z1 || blocked(px, pz)) continue;
    const s = rng.range(size[0], size[1]);
    const g = chamferBox(s * rng.range(0.8, 1.5), s * rng.range(0.4, 0.8), s * rng.range(0.8, 1.6), s * 0.22);
    const v = rng.range(0.62, 1.14);
    w.batch.add(g, mat, {
      matrix: xform([px, y + s * 0.2, pz], [rng.range(-0.25, 0.25), rng.range(0, 3.14), rng.range(-0.25, 0.25)]),
      surface: surfOf(mat), uvScale: 0.2, cast: false,
      tint: [v * rng.range(0.98, 1.08), v, v * rng.range(0.9, 1.02)],
    });
    g.dispose();
  }
}

/**
 * Oil, damp and scrub marks. Soft ragged discs at low value, placed where a
 * vehicle or a stall would have stood — the aperiodic dark shapes that stop the
 * eye from locking onto the texture lattice at all.
 */
export function stainScatter(w, { x0, z0, x1, z1, y = 0.007, count = 8, mat = 'asphalt', dark = [0.38, 0.72] }) {
  const rng = w.rng;
  for (let i = 0; i < count; i++) {
    const cx = rng.range(x0, x1), cz = rng.range(z0, z1);
    const r0 = rng.range(0.5, 1.5);
    const v = rng.range(dark[0], dark[1]);
    // two nested discs: a core and a feathered halo, so the rim is not one line
    driftPatch(w, {
      x: cx, z: cz, rx: r0, rz: r0 * rng.range(0.6, 1.3), rot: rng.range(0, Math.PI),
      y, mat, surface: surfOf(mat), seg: 24, uvScale: 3.7,
      tint: [v, v * 0.99, v * 0.96],
    });
    driftPatch(w, {
      x: cx + rng.range(-0.4, 0.4), z: cz + rng.range(-0.4, 0.4),
      rx: r0 * rng.range(1.5, 2.4), rz: r0 * rng.range(1.2, 2.2), rot: rng.range(0, Math.PI),
      y: y - 0.001, mat, surface: surfOf(mat), seg: 22, uvScale: 5.9,
      tint: [(v + 1) / 2, (v + 1) / 2 * 0.99, (v + 1) / 2 * 0.97],
    });
  }
}

/** A pair of dried tyre tracks — long aperiodic streaks across a big flat. */
export function tyreTracks(w, { a, b, gauge = 1.62, width = 0.22, y = 0.008, mat = 'asphalt', tint = [0.62, 0.60, 0.58] }) {
  const rng = w.rng;
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const rotY = Math.atan2(-(b[1] - a[1]), b[0] - a[0]);
  const nx = -Math.sin(-rotY), nz = -Math.cos(-rotY);
  const segs = Math.max(2, Math.round(L / 2.4));
  for (const s of [-1, 1]) {
    for (let i = 0; i < segs; i++) {
      if (rng() < 0.18) continue;             // the track fades in and out
      const t0 = i / segs, t1 = (i + 1) / segs;
      const m = (t0 + t1) / 2;
      const px = a[0] + (b[0] - a[0]) * m + nx * s * gauge / 2;
      const pz = a[1] + (b[1] - a[1]) * m + nz * s * gauge / 2;
      const g = quadXZ(L / segs * 1.02, width * rng.range(0.8, 1.2));
      const v = rng.range(0.85, 1.15);
      w.batch.add(g, mat, {
        matrix: xform([px, y, pz], rotY + rng.range(-0.02, 0.02)),
        surface: surfOf(mat), uvScale: 1.3, cast: false,
        tint: [tint[0] * v, tint[1] * v, tint[2] * v],
      });
      g.dispose();
    }
  }
}

/** Raised pavement slab with a chamfered kerb face all round. */
export function pavement(w, { x0, z0, x1, z1, h = 0.16, mat = 'concrete_floor', surface = 'concrete', res = 0.9, kerbs = 'all', tint = null }) {
  const X0 = Math.min(x0, x1), X1 = Math.max(x0, x1);
  const Z0 = Math.min(z0, z1), Z1 = Math.max(z0, z1);
  // the pavement's own blockerBox below covers the full slab, so the patch does
  // not need to add a second (thinner, coincident) proxy
  groundPatch(w, { x0: X0, z0: Z0, x1: X1, z1: Z1, y: h, mat, surface, res, tint, uvScale: 1.6, collide: false });
  // kerb band
  const sides = kerbs === 'all' ? ['n', 's', 'e', 'w'] : (kerbs || []);
  for (const s of sides) {
    let a, b;
    if (s === 'n') { a = [X0, Z0]; b = [X1, Z0]; }
    else if (s === 's') { a = [X0, Z1]; b = [X1, Z1]; }
    else if (s === 'w') { a = [X0, Z0]; b = [X0, Z1]; }
    else { a = [X1, Z0]; b = [X1, Z1]; }
    kerbRun(w, { a, b, h, y: 0 });
  }
  w.blockerBox(X0, 0, Z0, X1, h, Z1, surface, false);
}

/** Chamfered kerb stones with occasional cracked/displaced units. */
export function kerbRun(w, { a, b, h = 0.16, y = 0, wdt = 0.26, mat = 'concrete_wall', unit = 1.1 }) {
  const rng = w.rng;
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const n = Math.max(1, Math.round(L / unit));
  const dir = [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
  const rotY = Math.atan2(-dir[1], dir[0]);
  for (let i = 0; i < n; i++) {
    const t0 = (i + 0.5) / n;
    const px = a[0] + (b[0] - a[0]) * t0;
    const pz = a[1] + (b[1] - a[1]) * t0;
    const broken = rng() < 0.14;
    const g = kerbProfile(L / n - 0.02, wdt, h + (broken ? -0.03 : 0), 0.05);
    w.batch.add(g, mat, {
      matrix: xform([px, y + (broken ? -0.02 : 0), pz], rotY + (broken ? rng.range(-0.05, 0.05) : 0)),
      surface: 'concrete', uvScale: 0.9,
      tint: [rng.range(0.92, 1.06), rng.range(0.92, 1.04), rng.range(0.9, 1.02)],
    });
    g.dispose();
  }
  w.blocker((a[0] + b[0]) / 2, y + h / 2, (a[1] + b[1]) / 2, L, h, wdt, rotY, 'concrete', false);
}

/** Manhole cover / drain grate. */
export function manhole(w, { x, z, y = 0.01, r = 0.36, grate = false }) {
  const g = grate ? chamferBox(r * 1.6, 0.07, r * 1.1, 0.02) : chamferCyl(r, 0.08, 14, 0.024);
  w.batch.add(g, grate ? 'metal_grate' : 'metal_rusted', {
    matrix: xform([x, y, z], w.rng.range(0, Math.PI)), surface: 'metal', uvScale: 0.5, cast: false,
  });
  g.dispose();
  const ring = chamferCyl(r + 0.09, 0.06, 16, 0.02);
  w.batch.add(ring, 'concrete_wall', { matrix: xform([x, y - 0.02, z], 0), surface: 'concrete', uvScale: 0.5, cast: false });
  ring.dispose();
}

/** Hairline cracks and tar seams — thin dark quads, no z-fight (offset in Y). */
export function crackScatter(w, { x0, z0, x1, z1, count = 20, y = 0.012, mat = 'asphalt' }) {
  const rng = w.rng;
  for (let i = 0; i < count; i++) {
    const cx = rng.range(x0, x1), cz = rng.range(z0, z1);
    const segs = rng.int(2, 4);
    let px = cx, pz = cz, ang = rng.range(0, Math.PI * 2);
    for (let s = 0; s < segs; s++) {
      const len = rng.range(0.6, 2.4);
      const nx = px + Math.cos(ang) * len, nz = pz + Math.sin(ang) * len;
      const g = quadXZ(len, rng.range(0.035, 0.09));
      w.batch.add(g, mat, {
        matrix: xform([(px + nx) / 2, y, (pz + nz) / 2], -ang),
        surface: 'concrete', uvScale: 0.4, cast: false, tint: [0.34, 0.32, 0.3],
      });
      g.dispose();
      px = nx; pz = nz; ang += rng.range(-0.7, 0.7);
    }
  }
}

/** Faded painted road marking. */
export function roadLine(w, { a, b, width = 0.14, y = 0.014, dash = 0, mat = 'concrete_floor' }) {
  const L = segLength([a[0], 0, a[1]], [b[0], 0, b[1]]);
  const rotY = Math.atan2(-(b[1] - a[1]), b[0] - a[0]);
  const n = dash ? Math.max(1, Math.round(L / (dash * 2))) : 1;
  for (let i = 0; i < n; i++) {
    const t = dash ? (i + 0.25) / n : 0.5;
    const seg = dash ? dash : L;
    const px = a[0] + (b[0] - a[0]) * t, pz = a[1] + (b[1] - a[1]) * t;
    const g = quadXZ(seg, width);
    w.batch.add(g, mat, {
      matrix: xform([px, y, pz], rotY), surface: 'concrete', uvScale: 0.4, cast: false,
      tint: [1.35, 1.3, 1.15],
    });
    g.dispose();
  }
}

/** Low concrete step/threshold — the small ledges that make a street read. */
export function step(w, { x, z, rotY = 0, width = 2.0, depth = 0.6, h = 0.14, mat = 'concrete_wall' }) {
  const g = chamferBox(width, h, depth, 0.04);
  w.batch.add(g, mat, { matrix: xform([x, h / 2, z], rotY), surface: 'concrete', uvScale: 0.9 });
  g.dispose();
  w.blocker(x, h / 2, z, width, h, depth, rotY, 'concrete', false);
}

/** Concrete jersey barrier — classic waist/head-glitch cover. */
export function jerseyBarrier(w, { x, z, y = 0, rotY = 0, len = 2.4, mat = 'concrete_wall' }) {
  const s = new THREE.Shape();
  s.moveTo(-0.30, 0); s.lineTo(0.30, 0); s.lineTo(0.24, 0.10);
  s.lineTo(0.13, 0.36); s.lineTo(0.11, 0.90); s.lineTo(-0.11, 0.90);
  s.lineTo(-0.13, 0.36); s.lineTo(-0.24, 0.10); s.closePath();
  const g = new THREE.ExtrudeGeometry(s, { depth: len - 0.08, bevelEnabled: true, bevelSize: 0.042, bevelThickness: 0.042, bevelSegments: 1, steps: 1 });
  g.translate(0, 0, -(len - 0.08) / 2);
  g.rotateY(Math.PI / 2);
  w.batch.add(g, mat, { matrix: xform([x, y, z], rotY), surface: 'concrete', uvScale: 0.8 });
  g.dispose();
  w.blocker(x, y + 0.45, z, len, 0.9, 0.6, rotY, 'concrete');
}

/** Steel bollard row. */
export function bollards(w, { a, b, count = 5, h = 0.95, r = 0.075, mat = 'metal_painted' }) {
  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0.5 : i / (count - 1);
    const x = a[0] + (b[0] - a[0]) * t, z = a[1] + (b[1] - a[1]) * t;
    const g = chamferCyl(r, h, 10, 0.028);
    w.batch.add(g, mat, { matrix: xform([x, h / 2, z], 0), surface: 'metal', uvScale: 0.4 });
    g.dispose();
    const cap = chamferCyl(r * 0.9, 0.055, 10, 0.02);
    w.batch.add(cap, 'metal_painted', { matrix: xform([x, h + 0.02, z], 0), surface: 'metal', uvScale: 0.2 });
    cap.dispose();
    w.blocker(x, h / 2, z, r * 2.2, h, r * 2.2, 0, 'metal', false);
  }
}
